import { ReactNode, useCallback, useMemo } from 'react';
import { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { Theme, ThemeProvider, createTheme } from '@mui/material/styles';
import type { GridLocaleText } from '@mui/x-data-grid';

/**
 * Routes the MUI X DataGrid's UI strings through Lingui.
 *
 * The grid merges this map over its own English, so a key omitted here is not a blank label
 * but an untranslated one — cover every key the grid has.
 *
 * `MuiTablePagination` is the one the grid does not own: its default is `{}`, and the
 * footer's labels come from `@mui/material`'s `TablePagination`.
 */
export function buildDataGridLocalization(i18n: I18n): GridLocaleText {
  return {
    noRowsLabel: i18n.t('No rows'),
    noResultsOverlayLabel: i18n.t('No results found.'),
    toolbarDensity: i18n.t('Density'),
    toolbarDensityLabel: i18n.t('Density'),
    toolbarDensityCompact: i18n.t('Compact'),
    toolbarDensityStandard: i18n.t('Standard'),
    toolbarDensityComfortable: i18n.t('Comfortable'),
    toolbarColumns: i18n.t('Columns'),
    toolbarColumnsLabel: i18n.t('Select columns'),
    toolbarFilters: i18n.t('Filters'),
    toolbarFiltersLabel: i18n.t('Show filters'),
    toolbarFiltersTooltipHide: i18n.t('Hide filters'),
    toolbarFiltersTooltipShow: i18n.t('Show filters'),
    toolbarFiltersTooltipActive: (count) => i18n.t('{count} active filter(s)', { count }),
    toolbarQuickFilterPlaceholder: i18n.t('Search…'),
    toolbarQuickFilterLabel: i18n.t('Search'),
    toolbarQuickFilterDeleteIconLabel: i18n.t('Clear'),
    toolbarExport: i18n.t('Export'),
    toolbarExportLabel: i18n.t('Export'),
    toolbarExportCSV: i18n.t('Download as CSV'),
    toolbarExportPrint: i18n.t('Print'),
    toolbarExportExcel: i18n.t('Download as Excel'),
    columnsManagementSearchTitle: i18n.t('Search'),
    columnsManagementNoColumns: i18n.t('No columns'),
    columnsManagementShowHideAllText: i18n.t('Show/Hide All'),
    columnsManagementReset: i18n.t('Reset'),
    filterPanelAddFilter: i18n.t('Add filter'),
    filterPanelRemoveAll: i18n.t('Remove all'),
    filterPanelDeleteIconLabel: i18n.t('Delete'),
    filterPanelLogicOperator: i18n.t('Logic operator'),
    filterPanelOperator: i18n.t('Operator'),
    filterPanelOperatorAnd: i18n.t('And'),
    filterPanelOperatorOr: i18n.t('Or'),
    filterPanelColumns: i18n.t('Columns'),
    filterPanelInputLabel: i18n.t('Value'),
    filterPanelInputPlaceholder: i18n.t('Filter value'),
    filterOperatorContains: i18n.t('contains'),
    filterOperatorDoesNotContain: i18n.t('does not contain'),
    filterOperatorEquals: i18n.t('equals'),
    filterOperatorDoesNotEqual: i18n.t('does not equal'),
    filterOperatorStartsWith: i18n.t('starts with'),
    filterOperatorEndsWith: i18n.t('ends with'),
    filterOperatorIs: i18n.t('is'),
    filterOperatorNot: i18n.t('is not'),
    filterOperatorAfter: i18n.t('is after'),
    filterOperatorOnOrAfter: i18n.t('is on or after'),
    filterOperatorBefore: i18n.t('is before'),
    filterOperatorOnOrBefore: i18n.t('is on or before'),
    filterOperatorIsEmpty: i18n.t('is empty'),
    filterOperatorIsNotEmpty: i18n.t('is not empty'),
    filterOperatorIsAnyOf: i18n.t('is any of'),
    // Mathematical symbols: no language renders them differently.
    'filterOperator=': '=',
    'filterOperator!=': '!=',
    'filterOperator>': '>',
    'filterOperator>=': '>=',
    'filterOperator<': '<',
    'filterOperator<=': '<=',
    headerFilterOperatorContains: i18n.t('Contains'),
    headerFilterOperatorDoesNotContain: i18n.t('Does not contain'),
    headerFilterOperatorEquals: i18n.t('Equals'),
    headerFilterOperatorDoesNotEqual: i18n.t('Does not equal'),
    headerFilterOperatorStartsWith: i18n.t('Starts with'),
    headerFilterOperatorEndsWith: i18n.t('Ends with'),
    headerFilterOperatorIs: i18n.t('Is'),
    headerFilterOperatorNot: i18n.t('Is not'),
    headerFilterOperatorAfter: i18n.t('Is after'),
    headerFilterOperatorOnOrAfter: i18n.t('Is on or after'),
    headerFilterOperatorBefore: i18n.t('Is before'),
    headerFilterOperatorOnOrBefore: i18n.t('Is on or before'),
    headerFilterOperatorIsEmpty: i18n.t('Is empty'),
    headerFilterOperatorIsNotEmpty: i18n.t('Is not empty'),
    headerFilterOperatorIsAnyOf: i18n.t('Is any of'),
    'headerFilterOperator=': i18n.t('Equals'),
    'headerFilterOperator!=': i18n.t('Not equals'),
    'headerFilterOperator>': i18n.t('Greater than'),
    'headerFilterOperator>=': i18n.t('Greater than or equal to'),
    'headerFilterOperator<': i18n.t('Less than'),
    'headerFilterOperator<=': i18n.t('Less than or equal to'),
    filterValueAny: i18n.t('any'),
    filterValueTrue: i18n.t('true'),
    filterValueFalse: i18n.t('false'),
    columnMenuLabel: i18n.t('Menu'),
    columnMenuShowColumns: i18n.t('Show columns'),
    columnMenuManageColumns: i18n.t('Manage columns'),
    columnMenuFilter: i18n.t('Filter'),
    columnMenuHideColumn: i18n.t('Hide column'),
    columnMenuUnsort: i18n.t('Unsort'),
    columnMenuSortAsc: i18n.t('Sort by ASC'),
    columnMenuSortDesc: i18n.t('Sort by DESC'),
    columnHeaderFiltersTooltipActive: (count) => i18n.t('{count} active filter(s)', { count }),
    columnHeaderFiltersLabel: i18n.t('Show filters'),
    columnHeaderSortIconLabel: i18n.t('Sort'),
    footerRowSelected: (count) =>
      i18n.t('{count} row(s) selected', { count: count.toLocaleString(i18n.locale) }),
    footerTotalRows: i18n.t('Total Rows:'),
    footerTotalVisibleRows: (visibleCount, totalCount) =>
      i18n.t('{visibleCount} of {totalCount}', {
        visibleCount: visibleCount.toLocaleString(i18n.locale),
        totalCount: totalCount.toLocaleString(i18n.locale),
      }),
    checkboxSelectionHeaderName: i18n.t('Checkbox selection'),
    checkboxSelectionSelectAllRows: i18n.t('Select all rows'),
    checkboxSelectionUnselectAllRows: i18n.t('Unselect all rows'),
    checkboxSelectionSelectRow: i18n.t('Select row'),
    checkboxSelectionUnselectRow: i18n.t('Unselect row'),
    booleanCellTrueLabel: i18n.t('yes'),
    booleanCellFalseLabel: i18n.t('no'),
    actionsCellMore: i18n.t('more'),
    pinToLeft: i18n.t('Pin to left'),
    pinToRight: i18n.t('Pin to right'),
    unpin: i18n.t('Unpin'),
    treeDataGroupingHeaderName: i18n.t('Group'),
    treeDataExpand: i18n.t('see children'),
    treeDataCollapse: i18n.t('hide children'),
    groupingColumnHeaderName: i18n.t('Group'),
    groupColumn: (name) => i18n.t('Group by {name}', { name }),
    unGroupColumn: (name) => i18n.t('Stop grouping by {name}', { name }),
    detailPanelToggle: i18n.t('Detail panel toggle'),
    expandDetailPanel: i18n.t('Expand'),
    collapseDetailPanel: i18n.t('Collapse'),
    MuiTablePagination: {
      labelRowsPerPage: i18n.t('Rows per page:'),
      labelDisplayedRows: ({ from, to, count, estimated }) => {
        if (count !== -1) {
          return i18n.t('{from}–{to} of {count}', { from, to, count });
        }
        const upperBound = estimated && estimated > to ? estimated : to;
        return i18n.t('{from}–{to} of more than {upperBound}', { from, to, upperBound });
      },
      getItemAriaLabel: (type) => {
        switch (type) {
          case 'first':
            return i18n.t('Go to first page');
          case 'last':
            return i18n.t('Go to last page');
          case 'next':
            return i18n.t('Go to next page');
          default:
            return i18n.t('Go to previous page');
        }
      },
    },
    rowReorderingHeaderName: i18n.t('Row reordering'),
    aggregationMenuItemHeader: i18n.t('Aggregation'),
    aggregationFunctionLabelSum: i18n.t('sum'),
    aggregationFunctionLabelAvg: i18n.t('avg'),
    aggregationFunctionLabelMin: i18n.t('min'),
    aggregationFunctionLabelMax: i18n.t('max'),
    aggregationFunctionLabelSize: i18n.t('size'),
  };
}

/** The grid dictionary for the active language, rebuilt when that language changes. */
export function useDataGridLocaleText(): GridLocaleText {
  const { i18n } = useLingui();

  // `i18n` is a stable instance whose `locale` changes on activation, so the
  // locale — not the instance — is what must invalidate the built strings.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => buildDataGridLocalization(i18n), [i18n, i18n.locale]);
}

/**
 * Puts the dictionary on the theme, so every grid in the tree is translated by default.
 *
 * `useDataGridProps` reads `useThemeProps({ name: 'MuiDataGrid' })` and merges the result
 * over the grid's English, so a grid added later is covered without touching it. A call
 * site that passes its own `localeText` still wins — props beat `defaultProps`.
 *
 * Mount it inside the `I18nProvider`, which is what supplies the active language.
 */
export function DataGridLocalizationProvider({ children }: { children: ReactNode }) {
  const localeText = useDataGridLocaleText();

  const withDictionary = useCallback(
    (outerTheme: Theme) =>
      createTheme(outerTheme, {
        components: { MuiDataGrid: { defaultProps: { localeText } } },
      }),
    [localeText]
  );

  return <ThemeProvider theme={withDictionary}>{children}</ThemeProvider>;
}
